/**
 * Callable function request/response contracts (client <-> functions).
 */

import type { AgentToolName, ToolConfirmationRequired, ToolResult } from './agent'
import type {
  ConnectedAccountSlotId,
  EpochMillis,
  RecurrenceRule,
  UsageDailyDoc,
  UserDoc,
  UserFeatureFlags,
} from './models'

/** sendMessage — send a chat turn; the assistant reply streams into Firestore. */
export interface SendMessageRequest {
  conversationId?: string
  text: string
  attachments?: Array<{ name: string; storagePath: string; mimeType: string; size: number }>
}
export interface SendMessageResponse {
  conversationId: string
  userMessageId: string
  assistantMessageId: string
}

/** getMicrosoftAuthUrl — begin the "Connect Microsoft 365" OAuth flow. */
export interface GetMicrosoftAuthUrlRequest {
  /** Absolute URL to return to after the OAuth callback completes. */
  returnTo?: string
}
export interface GetMicrosoftAuthUrlResponse {
  authUrl: string
}

/** disconnectMicrosoft — revoke/forget the stored refresh token. */
export interface DisconnectMicrosoftResponse {
  disconnected: boolean
}

/** syncMicrosoftPhoto — fetch the Graph profile photo and store it on the user doc. */
export interface SyncMicrosoftPhotoResponse {
  /** The stored photo as a `data:` URL, or null when the mailbox has none. */
  photoUrl: string | null
}

/** createReminder (direct callable, mirrors the agent tool). */
export interface CreateReminderRequest {
  title: string
  dueISO: string
  description?: string
  recurrence?: RecurrenceRule
  /** Marca o lembrete como urgente (ênfase no texto composto); não altera os canais de entrega. */
  urgent?: boolean
}
export interface CreateReminderResponse {
  id: string
}

/** getUploadUrl — signed URL for direct attachment upload to Storage. */
export interface GetUploadUrlRequest {
  fileName: string
  mimeType: string
}
export interface GetUploadUrlResponse {
  uploadUrl: string
  storagePath: string
}

/**
 * executeAgentTool — executes one server-side agent tool on behalf of the
 * client-side voice session (Gemini Live). Tools stay 100% server-side; the
 * client only relays the model's function calls here.
 */
export interface ExecuteAgentToolRequest {
  name: AgentToolName
  args: Record<string, unknown>
  /**
   * Confirmação explícita de uma ação de escrita. Ausente/false na primeira
   * chamada (a tool de escrita retorna ToolConfirmationRequired); true reenvia a
   * mesma chamada para executar de fato. Ignorado por tools de leitura.
   */
  confirmed?: boolean
  /**
   * Conversa de origem da chamada. Necessária para tools que postam um resultado
   * assíncrono de volta no chat (ex.: startEmailResearch, cujo job em background
   * grava o relatório como mensagem nesta conversa). Ignorada pelas demais tools.
   */
  conversationId?: string
  /**
   * Mensagem do usuário que originou a ação. Usada por tools que gravam o insumo
   * de origem (ex.: createReminder → sourceContext.summary). Ignorada pelas demais.
   */
  userText?: string
  /**
   * Chave de idempotência ESTÁVEL da confirmação (mesma em retries, reconexões e
   * duplo clique). Uma confirmação = uma execução: o backend só executa a ação de
   * escrita uma vez por chave e devolve o resultado em cache nas repetições.
   * Voz: `call.id` da function call do Live; chat: `${conversationId}:${messageId}:${partIndex}`.
   * Só vale para tools de escrita com `confirmed:true`; ignorada nas demais.
   */
  idempotencyKey?: string
}
export interface ExecuteAgentToolResponse {
  /**
   * `ToolConfirmationRequired` quando uma tool de escrita foi chamada sem
   * `confirmed` (nada foi executado); caso contrário o envelope normal da tool.
   * Discrimine por `needsConfirmation`.
   */
  result: ToolResult<unknown> | ToolConfirmationRequired
  /**
   * `true` quando a chamada foi DEDUPLICADA no servidor (a ação já havia sido
   * executada por uma confirmação anterior) e `result` é o resultado em cache —
   * nada foi executado de novo. Ausente/`false` no caminho normal.
   */
  deduped?: boolean
}

/** getVoiceSessionConfig — model id, system prompt and voice for the client-side Live session. */
export interface GetVoiceSessionConfigRequest {
  /**
   * Conversa da sessão de voz, quando iniciada a partir de uma conversa
   * existente. Usada para filtrar as ferramentas desabilitadas naquela conversa
   * (disabledTools). Ausente numa sessão nova (a conversa é criada depois, com
   * todas as ferramentas habilitadas).
   */
  conversationId?: string
}
export interface GetVoiceSessionConfigResponse {
  model: string
  systemPrompt: string
  voiceName: string
  /**
   * Ferramentas desabilitadas na conversa de origem (ids de AgentToolName). O
   * cliente remove estas declarações antes de abrir a sessão Live. Ausente/vazio
   * ⇒ todas habilitadas.
   */
  disabledTools?: string[]
}

/** refreshAdminClaim — reconcilia o claim de admin do chamador (request vazio). */
export interface RefreshAdminClaimResponse {
  updated: boolean
  isAdmin: boolean
}

/**
 * adminSetUserFeatures — liga/desliga flags de feature POR USUÁRIO (`UserFeatureFlags`),
 * gravadas em `users/{uid}.features`. `features` é um PATCH parcial (merge aninhado no
 * backend — nunca dotted keys); campos ausentes preservam o valor atual.
 */
export interface AdminSetUserFeaturesRequest {
  uid: string
  features: UserFeatureFlags
}
export interface AdminSetUserFeaturesResponse {
  success: true
  features: UserFeatureFlags
}

/** Resumo de um usuário para a listagem administrativa. */
export interface AdminUserSummary {
  uid: string
  email: string
  displayName?: string
  allowed: boolean
  createdAt: EpochMillis
  usage?: {
    totalInputTokens: number
    totalOutputTokens: number
    totalCostBRL: number
  }
  /** Flags de feature por usuário (ausente ⇒ nenhuma habilitada — ver `isUserFeatureEnabled`). */
  features?: UserFeatureFlags
}

/** adminListUsers — lista paginada de usuários com uso/custo agregado. */
export interface AdminListUsersRequest {
  limit?: number
  cursor?: string
}
export interface AdminListUsersResponse {
  users: AdminUserSummary[]
  nextCursor?: string
  aggregate: {
    totalUsers: number
    totalInputTokens: number
    totalOutputTokens: number
    totalCostBRL: number
  }
}

/** adminGetUser — detalhe de um usuário, incluindo seu uso diário. */
export interface AdminGetUserRequest {
  uid: string
}
export interface AdminGetUserResponse {
  user: UserDoc
  allowed: boolean
  admin: boolean
  usageDaily: UsageDailyDoc[]
}

/** adminGetPricing — parâmetros de precificação vigentes (request vazio). */
export interface AdminGetPricingResponse {
  usdToBrlRate: number
  modelPricing: Record<string, { input: number; output: number }>
}

/** adminUpdatePricing — atualiza a cotação USD→BRL usada no cálculo de custo. */
export interface AdminUpdatePricingRequest {
  usdToBrlRate: number
}
export interface AdminUpdatePricingResponse {
  success: true
}

/** recordVoiceUsage — registra o consumo de áudio (segundos) de uma sessão de voz. */
export interface RecordVoiceUsageRequest {
  inputAudioSeconds: number
  outputAudioSeconds: number
}
export interface RecordVoiceUsageResponse {
  success: true
}

/** linkWhatsapp — vincula um número de WhatsApp ao usuário (via contato GHL). */
export interface LinkWhatsappRequest {
  phoneE164: string
}
export interface LinkWhatsappResponse {
  contactId: string
  phoneE164: string
}

/** unlinkWhatsapp — remove o vínculo de WhatsApp do usuário (request vazio). */
export type UnlinkWhatsappRequest = Record<string, never>
export interface UnlinkWhatsappResponse {
  success: true
}

/** getGoogleAuthUrl — inicia a conexão da conta Google (fonte de dados adicional). */
export interface GetGoogleAuthUrlRequest {
  /** Absolute URL to return to after the OAuth callback completes. */
  returnTo?: string
}
export interface GetGoogleAuthUrlResponse {
  authUrl: string
}

/** getMicrosoftExtraAuthUrl — inicia a conexão de uma conta Microsoft CORPORATIVA extra. */
export interface GetMicrosoftExtraAuthUrlRequest {
  /** Absolute URL to return to after the OAuth callback completes. */
  returnTo?: string
  /**
   * Slot ms-extra específico a (re)conectar — usado pelo botão "Reconectar" de
   * um card já vinculado, para que a autenticação volte para ELE mesmo que
   * outro slot também esteja livre. Ausente ⇒ comportamento atual (aloca o
   * primeiro slot livre — usado pelo botão genérico "Conectar conta Microsoft
   * adicional"). Deve ser um slot ms-extra-N do PRÓPRIO uid com
   * status !== 'connected' — a callable valida e o callback revalida (ver
   * oauthState.ts#OAuthStatePayload.targetSlotId).
   */
  targetSlotId?: ConnectedAccountSlotId
}
export interface GetMicrosoftExtraAuthUrlResponse {
  authUrl: string
}

/** disconnectConnectedAccount — desconecta UMA conta extra (Google ou MS-extra) por slot. */
export interface DisconnectConnectedAccountRequest {
  slotId: ConnectedAccountSlotId
}
export interface DisconnectConnectedAccountResponse {
  disconnected: true
}

/**
 * sendChannelTest — dispara uma mensagem de teste avulsa num canal de entrega
 * (Teams/e-mail/WhatsApp), sob demanda, independente de rotinas/cron. Testa a
 * entregabilidade do canal em si — NÃO exige que
 * `preferences.deliveryChannels.<channel>` esteja ligado.
 */
export interface SendChannelTestRequest {
  channel: 'teams' | 'email' | 'whatsapp'
}
export interface SendChannelTestResponse {
  delivered: boolean
  /** Só para WhatsApp: qual caminho foi usado. */
  via?: 'sendText' | 'sendTemplate'
  /** Motivo em pt-BR quando `delivered` é false. */
  error?: string
}

/** Callable names (single source of truth for httpsCallable()). */
export const CALLABLES = {
  sendMessage: 'sendMessage',
  getMicrosoftAuthUrl: 'getMicrosoftAuthUrl',
  disconnectMicrosoft: 'disconnectMicrosoft',
  syncMicrosoftPhoto: 'syncMicrosoftPhoto',
  createReminder: 'createReminder',
  getUploadUrl: 'getUploadUrl',
  executeAgentTool: 'executeAgentTool',
  getVoiceSessionConfig: 'getVoiceSessionConfig',
  refreshAdminClaim: 'refreshAdminClaim',
  adminListUsers: 'adminListUsers',
  adminGetUser: 'adminGetUser',
  adminGetPricing: 'adminGetPricing',
  adminUpdatePricing: 'adminUpdatePricing',
  adminSetUserFeatures: 'adminSetUserFeatures',
  recordVoiceUsage: 'recordVoiceUsage',
  linkWhatsapp: 'linkWhatsapp',
  unlinkWhatsapp: 'unlinkWhatsapp',
  getGoogleAuthUrl: 'getGoogleAuthUrl',
  getMicrosoftExtraAuthUrl: 'getMicrosoftExtraAuthUrl',
  disconnectConnectedAccount: 'disconnectConnectedAccount',
  sendChannelTest: 'sendChannelTest',
} as const

/** HTTPS function name for the Microsoft (primary) OAuth redirect callback. */
export const OAUTH_CALLBACK_FUNCTION = 'microsoftOauthCallback'

/** HTTPS function name for the Google (conta conectada) OAuth redirect callback. */
export const GOOGLE_OAUTH_CALLBACK_FUNCTION = 'googleOauthCallback'

/** HTTPS function name for the Microsoft extra (conta conectada) OAuth redirect callback. */
export const MS_EXTRA_OAUTH_CALLBACK_FUNCTION = 'microsoftExtraOauthCallback'
